import { ErrorResponseResult, FetchError, FetchResult } from 'src/types/TApiQueries';

export function queryFnFetchErrorWrapper<TData, TParams extends any[] = unknown[]>(
  fetchFn: (...params: TParams) => Promise<FetchResult<TData>>,
) {
  return async function (...params: Parameters<typeof fetchFn>) {
    try {
      const result = await fetchFn(...params);

      if (isErrorResponseResult(result)) {
        // Distinguish authentication from authorization:
        //   401 -> genuine auth failure (no / expired session): clear the
        //          stored session and bounce to login.
        //   403 -> authenticated but NOT permitted for this specific resource
        //          ("feature not permitted"). The backend returns 403 (never a
        //          302 -> /login) for that case, so we must NOT log the user
        //          out here — the calling component decides how to render it
        //          (hide/disable the widget). Logging out on 403 is exactly
        //          what made a USER-role login bounce straight back to login
        //          when the dashboard fired admin-scoped calls
        //          (/subscription, /settings/ai, /insights/destinations).
        //          See filespin-saas-backend filespin/api/base.py
        //          get_current_user().
        if (result.status === 401 && window.location.pathname !== '/login') {
          localStorage.removeItem('fs.auth.');
          window.location.reload();
        }
        throw result;
      }

      return result;
    } catch (error) {
      if (error instanceof Error || isErrorResponseResult(error)) {
        throw error;
      } else {
        throw new Error('unknown api error');
      }
    }
  };
}

export function isErrorResponseResult<TData>(fetchResult: FetchResult<any>): fetchResult is ErrorResponseResult {
  return fetchResult.hasOwnProperty('error') && fetchResult.hasOwnProperty('status');
}

/** Structured payload of a per-plan feature-gate 403 (plan-structure v2). */
export type TPlanUpgradeRequired = {
  feature: string;
  requiredPlan: string;
  message?: string;
  /**
   * Phase 9 feature trials. Computed centrally by the backend in
   * `write_plan_upgrade_required`, so every gate advertises a trial with no
   * per-gate work. `addonCode` is what we POST to
   * `/api/v1/features/{code}/trial`; `trialAvailable` is true only when this
   * account can actually start one (eligible plan, not already used, feature
   * trialable, flag on) — so the CTA is never shown where it would fail.
   */
  addonCode?: string;
  trialAvailable?: boolean;
  trialDays?: number;
};

/**
 * Detect the backend's `plan_upgrade_required` 403 (write_plan_upgrade_required
 * in filespin/api/base.py) on any fetch/query error. The error body arrives
 * either as a parsed JSON object on `result.error` (application/json) or not
 * at all (legacy string bodies) — both shapes are handled. Returns the
 * structured payload, or null when the error is anything else.
 */
export function planUpgradeRequired(err: unknown): TPlanUpgradeRequired | null {
  const raw = (err as { error?: unknown; status?: number } | null | undefined);
  if (!raw || raw.status !== 403) return null;
  const body = raw.error;
  if (
    body &&
    typeof body === 'object' &&
    (body as { error?: unknown }).error === 'plan_upgrade_required'
  ) {
    const b = body as {
      feature?: string;
      required_plan?: string;
      message?: string;
      addon_code?: string;
      trial_available?: boolean;
      trial_days?: number;
    };
    return {
      feature: b.feature ?? 'feature',
      requiredPlan: b.required_plan ?? '',
      message: b.message,
      addonCode: b.addon_code,
      trialAvailable: Boolean(b.trial_available),
      trialDays: b.trial_days,
    };
  }
  return null;
}

export function throwFetchError(error: FetchError | null) {
  if (isErrorResponseResult(error)) {
    throw new Error(error.message);
  }
  throw error;
}
